import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Checkbox, DatePicker, Pagination, Select, Table } from "antd";
import { useLocalStorage } from "react-use";

import { fetcher } from "../../data/fetcher";
import { useOrganization } from "../../data/use-organization";
import { WidgetContainer } from "./widget-container";

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25];
const DEFAULT_COLUMNS = [
  "name",
  "valueWon",
  "valueInProgressWeightedByActed",
  "valueInProgressUnweightedByActed",
];
// Add further columns as added to backend SQL, e.g., stage breakdowns

const getDefaultTopAccountsDateRange = () => {
  return [
    dayjs().subtract(90, "day").format("YYYY-MM-DD"),
    dayjs().format("YYYY-MM-DD"),
  ];
};

const getSortField = (sorter) => {
  if (!sorter) {
    return null;
  }

  if (typeof sorter.field === "string") {
    return sorter.field;
  }

  if (Array.isArray(sorter.field)) {
    return sorter.field.join(".");
  }

  if (typeof sorter.columnKey === "string") {
    return sorter.columnKey;
  }

  return null;
};

export const getDefaultTopAccountsConfig = () => {
  return {
    columns: DEFAULT_COLUMNS,
    showWon: true,
    showNotWon: true,
    weighted: false,
    dateRange: getDefaultTopAccountsDateRange(),
    sortField: "valueWon",
    sortOrder: "desc",
    perPage: 10,
    page: 1,
  };
};

const TopAccounts = () => {
  const organization = useOrganization();
  const defaultConfig = useMemo(() => {
    return getDefaultTopAccountsConfig();
  }, []);

  const [storedConfig, setStoredConfig] = useLocalStorage(
    `${organization._id}.LAYOUT_STATE.DASHBOARD.TOP_ACCOUNTS`,
    defaultConfig,
  );

  const config = useMemo(() => {
    return {
      ...defaultConfig,
      ...(storedConfig || {}),
    };
  }, [defaultConfig, storedConfig]);

  const showWon = config?.showWon ?? defaultConfig.showWon;
  const showNotWon = config?.showNotWon ?? defaultConfig.showNotWon;
  const weighted = config?.weighted ?? defaultConfig.weighted;
  const sortField = config?.sortField ?? defaultConfig.sortField;
  const sortOrder = config?.sortOrder ?? defaultConfig.sortOrder;
  const perPage = config?.perPage ?? defaultConfig.perPage;
  const page = config?.page ?? defaultConfig.page;
  const dateRange =
    Array.isArray(config?.dateRange) && config.dateRange.length === 2
      ? config.dateRange
      : defaultConfig.dateRange;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], total: 0 });

  const dateRangeStart = dateRange?.[0] ?? null;
  const dateRangeEnd = dateRange?.[1] ?? null;

  const rangePickerValue = useMemo(() => {
    return [
      dateRangeStart ? dayjs(dateRangeStart) : null,
      dateRangeEnd ? dayjs(dateRangeEnd) : null,
    ];
  }, [dateRangeEnd, dateRangeStart]);

  // Always-visible columns, no toggle, matching backend result fields
  // Determine which result columns to show based on toggles
  const columnsToShow = useMemo(() => {
    const cols = [
      {
        title: "Account Name",
        dataIndex: "accountName",
        key: "accountName",
        render: (text, record) => (
          <a
            href={`/accounts/${record._id}`}
            style={{ color: "#874d9a", textDecoration: "underline" }}
          >
            {text}
          </a>
        ),
        fixed: true,
      },
    ];
    if (showWon) {
      cols.push({
        title: "Won Value",
        dataIndex: "valueWon",
        key: "valueWon",
        render: (value) => (value == null ? "-" : `$${value.toLocaleString()}`),
      });
    }
    if (showNotWon) {
      cols.push({
        title: "In Progress (Weighted)",
        dataIndex: "valueInProgressWeightedByActed",
        key: "valueInProgressWeightedByActed",
        render: (value) => (value == null ? "-" : `$${value.toLocaleString()}`),
      });
    }
    if (weighted) {
      cols.push({
        title: "In Progress (Unweighted)",
        dataIndex: "valueInProgressUnweightedByActed",
        key: "valueInProgressUnweightedByActed",
        render: (value) => (value == null ? "-" : `$${value.toLocaleString()}`),
      });
    }
    return cols;
  }, [showWon, showNotWon, weighted]);

  const persistConfig = (changes) => {
    setStoredConfig({
      ...config,
      ...changes,
    });
  };

  const handleBooleanChange = (key, value) => {
    persistConfig({
      [key]: value,
      page: 1,
    });
  };

  const handleDateRangeChange = (values) => {
    const nextDateRange = Array.isArray(values)
      ? values.map((value) => {
          return value ? value.format("YYYY-MM-DD") : null;
        })
      : [null, null];

    persistConfig({
      dateRange: nextDateRange,
      page: 1,
    });
  };

  const handlePerPageChange = (value) => {
    persistConfig({
      perPage: Number(value),
      page: 1,
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);

      try {
        const response = await fetcher.get("/reporting/top-accounts", {
          query: {
            won: showWon,
            notWon: showNotWon,
            weighted,
            dateRangeStart,
            dateRangeEnd,
            sortOrder,
            sortField,
            perPage,
            page,
          },
        })();

        if (cancelled) {
          return;
        }

        setData(response || { rows: [], total: 0 });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [
    dateRangeEnd,
    dateRangeStart,
    page,
    perPage,
    showNotWon,
    showWon,
    sortField,
    sortOrder,
    weighted,
  ]);

  const tableProps = {
    bordered: false,
    columns: columnsToShow.map((col) =>
      col.dataIndex === "valueWon" ||
      col.dataIndex === "valueInProgressWeightedByActed" ||
      col.dataIndex === "valueInProgressUnweightedByActed"
        ? {
            ...col,
            sorter: true,
            sortOrder:
              sortField === col.dataIndex
                ? sortOrder === "asc"
                  ? "ascend"
                  : "descend"
                : null,
          }
        : col
    ),
    dataSource: data.rows,
    loading,
    pagination: false,
    rowKey: "_id",
    scroll: { x: true },
    size: "small",
    onChange: (_pagination, _filters, sorter) => {
      const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const nextField = getSortField(nextSorter);

      if (!nextField) {
        persistConfig({
          page: 1,
          sortField: defaultConfig.sortField,
          sortOrder: defaultConfig.sortOrder,
        });
        return;
      }

      persistConfig({
        page: 1,
        sortField: nextField,
        sortOrder: nextSorter.order === "ascend" ? "asc" : "desc",
      });
    },
    sortDirections: ["descend", "ascend"],
  };

  const empty = !loading && (!data.rows || !data.rows.length);

  return (
    <WidgetContainer>
      <div style={{ marginTop: 16, minWidth: 350 }}>
        <h3 style={{ fontSize: "1.1em", fontWeight: 600, marginBottom: 6 }}>
          Top Accounts
        </h3>

        <div
          style={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <Checkbox
            checked={showWon}
            onChange={() => handleBooleanChange("showWon", !showWon)}
          >
            Won Value
          </Checkbox>

          <Checkbox
            checked={showNotWon}
            onChange={() => handleBooleanChange("showNotWon", !showNotWon)}
          >
            In Progress (Weighted)
          </Checkbox>

          <Checkbox
            checked={weighted}
            onChange={() => handleBooleanChange("weighted", !weighted)}
          >
            In Progress (Unweighted)
          </Checkbox>

          <DatePicker.RangePicker
            value={rangePickerValue}
            onChange={handleDateRangeChange}
            style={{ minWidth: 220 }}
          />

          <Select
            options={PAGE_SIZE_OPTIONS.map((value) => {
              return { label: value, value };
            })}
            style={{ width: 80 }}
            value={perPage}
            onChange={handlePerPageChange}
          />
        </div>

        {empty ? (
          <div style={{ color: "#888", padding: 24, textAlign: "center" }}>
            No accounts found matching your filters.
          </div>
        ) : (
          <>
            <Table {...tableProps} />

            <Pagination
              current={page}
              pageSize={perPage}
              showSizeChanger={false}
              showTotal={(total) => {
                return `${total} account${total === 1 ? "" : "s"}`;
              }}
              style={{ float: "right", marginTop: 16 }}
              total={data.total}
              onChange={(nextPage) => {
                persistConfig({ page: nextPage });
              }}
            />
          </>
        )}
      </div>
    </WidgetContainer>
  );
};

export default TopAccounts;
